import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ActiveCycleResponse,
  costTotal,
  COST_STATE_LABELS,
  type CostInvoicesRequest,
  type CostInvoicesResponse,
  type CostState,
  dueFromTerms,
  formatMoney,
  INSTALLMENT_KIND_LABELS,
  INSTALLMENT_KINDS,
  type InstallmentKind,
  type InvoiceJobsResponse,
  type JobCost,
  type JobCostRequest,
  type JobReceivedInvoice,
  type JobResponse,
  type PassiveCycleResponse,
  parseDecimal,
  toCents,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { moneyOf } from '../people/people-ui.js';
import { ClientPicker } from '../quotes/ClientPicker.js';
import { CreditNoteMark, creditNoteRow } from '../treasury/treasury-ui.js';
import { InvoiceMatches } from './InvoiceMatches.js';
import { jobKeys, MoveButtons, RowAction } from './jobs-ui.js';
import { ProformaCell } from './ProformaCell.js';
import { MissingNote, ModeChoice, usePricing } from './RataLines.js';

const STATE_CLASS: Record<CostState, string> = {
  previsto: 'muted',
  parziale: 'info',
  consuntivato: 'warn',
  pagato: 'ok',
};

const passiveKey = (id: string) => [...jobKeys.all, 'passive-cycle', id] as const;

/**
 * The ciclo passivo of a commessa (2.04): the costs, from its offers or
 * written by hand, each with its supplier and the dates of ricevimento and
 * pagamento; the received invoices given to it. Amounts and dates change
 * until a cost is consuntivato. A madre shows those of its sottocommesse.
 */
export function PassiveCycle({ job }: { job: JobResponse }) {
  const project = job.kind === 'progetto';
  const queryClient = useQueryClient();
  const canTreasury = useCan('tesoreria.read');
  const [editing, setEditing] = useState<JobCost | 'new' | null>(null);
  const [removing, setRemoving] = useState<JobCost | null>(null);
  const [searching, setSearching] = useState<JobCost | null>(null);
  const [linking, setLinking] = useState<JobReceivedInvoice | null>(null);
  const cycle = useQuery({
    queryKey: passiveKey(job.id),
    queryFn: () => apiRequest<PassiveCycleResponse>(`/jobs/${job.id}/passive-cycle`),
  });
  const refresh = (data: PassiveCycleResponse) => {
    queryClient.setQueryData(passiveKey(job.id), data);
    return queryClient.invalidateQueries({ queryKey: jobKeys.all });
  };
  const settle = useMutation({
    mutationFn: ({ id, settled }: { id: string; settled: boolean }) =>
      apiRequest<PassiveCycleResponse>(`/jobs/${job.id}/costs/${id}/settled`, {
        method: 'PUT',
        body: { settled },
      }),
    onSuccess: refresh,
  });
  const release = useMutation({
    mutationFn: ({ id, released }: { id: string; released: boolean }) =>
      apiRequest<PassiveCycleResponse>(`/jobs/${job.id}/costs/${id}/release`, {
        method: 'POST',
        body: { released },
      }),
    onSuccess: refresh,
  });
  const move = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: 'up' | 'down' }) =>
      apiRequest<PassiveCycleResponse>(`/jobs/${job.id}/costs/${id}/move`, {
        method: 'POST',
        body: { direction },
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest<PassiveCycleResponse>(`/jobs/${job.id}/costs/${id}`, { method: 'DELETE' }),
    onSuccess: async (data) => {
      setRemoving(null);
      await refresh(data);
    },
  });
  const costs = useTableSort('job.passive.costs', cycle.data?.costs ?? [], {
    description: (row) => row.description,
    supplier: (row) => row.suppliers.map((supplier) => supplier.name).join(', ') || null,
    receiveOn: (row) => row.receiveOn,
    amount: (row) => amountSort(row.amount),
    payOn: (row) => row.payOn,
    state: (row) => COST_STATE_LABELS[row.state],
    invoiced: (row) => amountSort(row.invoiced),
  });
  const invoices = useTableSort('job.passive.invoices', cycle.data?.invoices ?? [], {
    number: (row) => row.number,
    jobCode: (row) => row.jobCode,
    subject: (row) => row.subject,
    date: (row) => row.date,
    taxable: (row) => amountSort(row.taxable),
    amount: (row) => amountSort(row.amount),
    cost: (row) => costName(row.costId),
    collected: (row) => amountSort(row.collected),
    nextDue: (row) => row.nextDue,
  });
  function costName(costId: string | null): string | null {
    return cycle.data?.costs.find((row) => row.id === costId)?.description ?? null;
  }

  if (cycle.isError) return <p className="error">{cycle.error.message}</p>;
  if (!cycle.data) return <p className="muted">Caricamento…</p>;
  const data = cycle.data;
  const editable = job.you.edit && !data.readOnly;
  const totalVat = data.costs.reduce((sum, row) => sum + Number(row.vat), 0);
  const withoutSupplier = data.costs.filter((row) => row.suppliers.length === 0).length;
  // Received invoices on the commessa but on no cost: the budget counts them on top (2.28).
  const withoutCost = data.invoices.filter(
    (row) => row.costIds.includes(null) && data.costs.some((cost) => cost.jobId === row.jobId),
  );
  const linkable = editable && canTreasury;

  return (
    <section className="stack">
      <div className="kpi-row">
        <div className="card kpi">
          <small className="muted">Costi previsti</small>
          <strong>{formatMoney(data.totals.planned)}</strong>
        </div>
        <div className="card kpi">
          <small className="muted">Consuntivati (imponibile)</small>
          <strong>{formatMoney(data.totals.invoiced)}</strong>
        </div>
        <div className="card kpi">
          <small className="muted">Pagato (con IVA)</small>
          <strong>{formatMoney(data.totals.paid)}</strong>
        </div>
        <div className="card kpi">
          <small className="muted">Da pagare (con IVA)</small>
          <strong>{formatMoney(data.totals.toPay)}</strong>
        </div>
      </div>
      {withoutSupplier > 0 && editable && (
        <p className="notice info">
          {withoutSupplier === 1 ? '1 costo non ha' : `${withoutSupplier} costi non hanno`} ancora
          il fornitore: sceglilo con «Scegli il fornitore…», il pagamento previsto segue i suoi
          termini. Poi «Cerca fatture» trova le sue fatture.
        </p>
      )}
      {withoutCost.length > 0 && (
        <p className="notice info">
          {withoutCost.length === 1
            ? '1 fattura è imputata'
            : `${withoutCost.length} fatture sono imputate`}{' '}
          {project ? 'al progetto' : 'alla commessa'} ma a nessun costo: il budget le conta in più
          rispetto ai costi previsti.
          {linkable && ' Collegale a un costo con «Collega al costo…».'}
        </p>
      )}
      {data.readOnly && (
        <p className="notice info">
          {project
            ? 'Il progetto ha sottoprogetti: qui la somma dei loro costi, in sola lettura. I costi si gestiscono nei sottoprogetti.'
            : 'La commessa ha sottocommesse: qui la somma dei loro costi, in sola lettura. I costi si gestiscono nelle sottocommesse.'}
        </p>
      )}

      <section className="card stack">
        <div className="row between">
          <h3>{project ? 'Costi esterni del progetto' : 'Costi della commessa'}</h3>
          {editable && (
            <button type="button" className="primary" onClick={() => setEditing('new')}>
              Nuovo costo
            </button>
          )}
        </div>
        {data.costs.length === 0 ? (
          <p className="muted">
            Nessun costo. Nascono dai costi previsti delle offerte collegate, oppure si aggiungono a
            mano.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <SortHeader sort={costs} column="description">
                    Costo
                  </SortHeader>
                  <SortHeader sort={costs} column="supplier">
                    Fornitore
                  </SortHeader>
                  <SortHeader sort={costs} column="receiveOn">
                    Ricevimento
                  </SortHeader>
                  <SortHeader sort={costs} column="amount" className="right">
                    Imponibile
                  </SortHeader>
                  <SortHeader sort={costs} column="payOn">
                    Pagamento
                  </SortHeader>
                  <SortHeader sort={costs} column="state">
                    Stato
                  </SortHeader>
                  <SortHeader sort={costs} column="invoiced" className="right">
                    Consuntivato
                  </SortHeader>
                  <th>Pro-forma</th>
                  {editable && <th />}
                </tr>
              </thead>
              <tbody>
                {costs.rows.map((row, index) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.description}</strong>
                      {row.isExpense && (
                        <>
                          {' '}
                          <span className="pill info">spesa</span>
                        </>
                      )}
                      {row.voice && (
                        <div className="small">
                          {row.voice.editions > 1 && `${row.voice.editions} edizioni × `}
                          {Number(row.voice.quantity).toLocaleString('it-IT')}{' '}
                          {row.voice.unitCode.toLowerCase()} × {formatMoney(row.voice.unitCost)}
                        </div>
                      )}
                      <div className="muted small">
                        {row.quote ? (
                          <Link to={`/offerte/${row.quote.id}`}>{row.quote.label}</Link>
                        ) : (
                          'a mano'
                        )}
                        {data.readOnly && (
                          <>
                            {' · '}
                            <Link to={`/commesse/${row.jobId}`}>{row.jobCode}</Link>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      {row.suppliers.length > 0 ? (
                        row.suppliers.map((supplier) => (
                          <div key={supplier.id}>{supplier.name}</div>
                        ))
                      ) : editable ? (
                        <button type="button" className="link" onClick={() => setEditing(row)}>
                          Scegli il fornitore…
                        </button>
                      ) : (
                        <span className="muted">da scegliere</span>
                      )}
                    </td>
                    <td className="nowrap">{formatDate(row.receiveOn)}</td>
                    <td className="right nowrap">
                      {formatMoney(row.amount)}
                      <div className="muted small">
                        IVA {formatMoney(row.vat)}
                        {Number(row.withholdingRate) !== 0 &&
                          ` · ritenuta ${Number(row.withholdingRate)} %`}
                      </div>
                      {row.settled && (
                        <div className="small" title={settledTitle(row)}>
                          saldato a {formatMoney(row.invoiced)}
                        </div>
                      )}
                    </td>
                    <td className="nowrap">
                      {formatDate(row.payOn)}
                      {row.termsCode && <div className="muted small">{row.termsCode}</div>}
                      {row.kind === 'erogazione' && (
                        <div className="muted small">
                          {INSTALLMENT_KIND_LABELS[row.kind]}
                          {row.releasedOn &&
                            ` · via libera ${formatDate(row.releasedOn)}${row.releasedBy ? `, ${row.releasedBy}` : ''}`}
                        </div>
                      )}
                      {row.contracted && <div className="small">contrattualizzato</div>}
                    </td>
                    <td>
                      <span className={`pill ${STATE_CLASS[row.state]}`}>
                        {COST_STATE_LABELS[row.state]}
                      </span>
                    </td>
                    <td className="right nowrap">{formatMoney(row.invoiced)}</td>
                    <td>
                      <ProformaCell<PassiveCycleResponse>
                        base={`/jobs/${job.id}/costs/${row.id}`}
                        proforma={row.proforma}
                        editable={editable}
                        subjectLabel="fornitore"
                        onChanged={refresh}
                      />
                    </td>
                    {editable && (
                      <td className="row-actions">
                        <MoveButtons
                          index={index}
                          count={costs.rows.length}
                          sorted={costs.sort !== null}
                          pending={move.isPending}
                          onMove={(direction) => move.mutate({ id: row.id, direction })}
                        />
                        {linkable &&
                          row.suppliers.length > 0 &&
                          !row.settled &&
                          row.state !== 'consuntivato' &&
                          row.state !== 'pagato' && (
                            <RowAction
                              icon="search"
                              label="Cerca fatture"
                              onClick={() => setSearching(row)}
                            />
                          )}
                        {row.state === 'parziale' && (
                          <RowAction
                            icon="check"
                            label="Saldo: non arriveranno altre fatture, il resto esce dal previsto"
                            disabled={settle.isPending}
                            onClick={() => settle.mutate({ id: row.id, settled: true })}
                          />
                        )}
                        {row.kind === 'erogazione' && !row.settled && (
                          <RowAction
                            icon={row.releasedOn ? 'undo' : 'check'}
                            label={
                              row.releasedOn
                                ? 'Togli il via libera: il costo torna previsto'
                                : 'Pagabile: dai il via libera'
                            }
                            disabled={release.isPending}
                            onClick={() =>
                              release.mutate({ id: row.id, released: !row.releasedOn })
                            }
                          />
                        )}
                        {row.settled && (
                          <RowAction
                            icon="undo"
                            label="Togli il saldo: il resto torna previsto"
                            disabled={settle.isPending}
                            onClick={() => settle.mutate({ id: row.id, settled: false })}
                          />
                        )}
                        <RowAction icon="edit" label="Modifica" onClick={() => setEditing(row)} />
                        {Number(row.invoiced) === 0 && (
                          <RowAction
                            icon="trash"
                            label="Togli il costo"
                            onClick={() => setRemoving(row)}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} className="muted">
                    Totale · IVA {formatMoney(totalVat)}
                  </td>
                  <td className="right nowrap">
                    <strong>{formatMoney(data.totals.planned)}</strong>
                  </td>
                  <td colSpan={2} />
                  <td className="right nowrap">{formatMoney(data.totals.invoiced)}</td>
                  <td />
                  {editable && <td />}
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {settle.isError && <p className="error">{settle.error.message}</p>}
        {move.isError && <p className="error">{move.error.message}</p>}
        <p className="muted small">
          Previsto → consuntivato (dalle fatture ricevute) → pagato. Con il «Saldo» un costo
          consuntivato in parte si chiude a quanto fatturato: il resto esce dal previsto. Cifre e
          date si correggono finché il costo non è consuntivato; poi valgono quelle delle fatture. I
          costi delle ore interne arriveranno con K-Presenze.
        </p>
      </section>

      <section className="card stack">
        <h3>Fatture ricevute {project ? 'del progetto' : 'della commessa'}</h3>
        {data.invoices.length === 0 ? (
          <p className="muted">Nessuna fattura imputata.</p>
        ) : (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <SortHeader sort={invoices} column="number">
                    Fattura
                  </SortHeader>
                  {data.readOnly && (
                    <SortHeader sort={invoices} column="jobCode">
                      {project ? 'Sottoprogetto' : 'Sottocommessa'}
                    </SortHeader>
                  )}
                  <SortHeader sort={invoices} column="subject">
                    Fornitore
                  </SortHeader>
                  <SortHeader sort={invoices} column="date">
                    Data
                  </SortHeader>
                  <SortHeader sort={invoices} column="taxable" className="right">
                    Imponibile fattura
                  </SortHeader>
                  <SortHeader sort={invoices} column="amount">
                    Ripartizione
                  </SortHeader>
                  <SortHeader sort={invoices} column="cost">
                    Costo
                  </SortHeader>
                  <SortHeader sort={invoices} column="collected" className="right">
                    Pagato
                  </SortHeader>
                  <SortHeader sort={invoices} column="nextDue">
                    Prossima scadenza
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {invoices.rows.map((row) => (
                  <tr key={row.allocationId} className={creditNoteRow(row)}>
                    <td>
                      <CreditNoteMark kind={row.kind} />
                      {canTreasury ? (
                        <Link to={`/tesoreria/fatture/${row.invoiceId}`}>{row.number}</Link>
                      ) : (
                        row.number
                      )}
                    </td>
                    {data.readOnly && <td>{row.jobCode}</td>}
                    <td>{row.subject}</td>
                    <td>{formatDate(row.date)}</td>
                    <td className="right">{formatMoney(row.taxable)}</td>
                    <td>
                      {row.sharedWith.length === 0 && row.amount === row.taxable
                        ? 'tutta qui'
                        : `${formatMoney(row.amount)} qui`}
                      {row.lines.length > 0 && (
                        <small className="muted">
                          {' '}
                          · {row.lines.length === 1 ? 'riga' : 'righe'}{' '}
                          {row.lines.map((position) => position + 1).join(', ')}
                        </small>
                      )}
                      {row.toReview && (
                        <>
                          {' '}
                          <span
                            className="pill warn"
                            title="Una riga è cambiata in Gestify dopo l’imputazione: rivedi la ripartizione sulla fattura"
                          >
                            da rivedere
                          </span>
                        </>
                      )}
                      {row.sharedWith.map((other) => (
                        <small key={other.jobId} className="muted">
                          {' '}
                          · {formatMoney(other.amount)} su{' '}
                          <Link to={`/commesse/${other.jobId}`}>{other.code}</Link>
                        </small>
                      ))}
                    </td>
                    <td>
                      {row.costIds
                        .flatMap((costId) => (costId ? [costName(costId)] : []))
                        .map((name, index) => (
                          <div key={index}>{name}</div>
                        ))}
                      {row.costIds.includes(null) && (
                        <>
                          <span className="muted">
                            {row.costIds.length > 1 ? 'una parte senza costo' : 'nessuno'}
                          </span>
                          {linkable &&
                            data.costs.some(
                              (cost) => cost.jobId === row.jobId && cost.suppliers.length > 0,
                            ) && (
                              <>
                                {' · '}
                                <button
                                  type="button"
                                  className="link"
                                  onClick={() => setLinking(row)}
                                >
                                  Collega al costo…
                                </button>
                              </>
                            )}
                        </>
                      )}
                    </td>
                    <td className="right">
                      {formatMoney(row.collected)}
                      <small className="muted"> di {formatMoney(row.due)}</small>
                    </td>
                    <td>
                      {row.nextDue ? (
                        formatDate(row.nextDue)
                      ) : (
                        <span className="pill ok">pagata</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {linkable && (
        <InvoiceMatches
          job={job}
          direction="ricevuta"
          targets={data.costs
            .filter((row) => !row.settled && Number(row.amount) - Number(row.invoiced) !== 0)
            .map((row) => ({
              id: row.id,
              label: row.description,
              open: (Number(row.amount) - Number(row.invoiced)).toFixed(2),
              subjectIds: row.suppliers.map((supplier) => supplier.id),
            }))}
        />
      )}

      {editing && (
        <CostDialog
          job={job}
          options={data.options}
          cost={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      {searching && (
        <CostInvoicesDialog
          job={job}
          cost={searching}
          costs={data.costs}
          onClose={() => setSearching(null)}
          onSaved={refresh}
        />
      )}
      {linking && (
        <LinkToCostDialog
          job={job}
          invoice={linking}
          costs={data.costs.filter(
            (cost) =>
              cost.jobId === linking.jobId &&
              cost.suppliers.length > 0 &&
              !cost.settled &&
              cost.state !== 'consuntivato' &&
              cost.state !== 'pagato',
          )}
          onClose={() => setLinking(null)}
          onSaved={refresh}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={`Togliere il costo «${removing.description}»?`}
          confirmLabel="Togli"
          busy={remove.isPending}
          error={remove.isError ? remove.error.message : null}
          onConfirm={() => remove.mutate(removing.id)}
          onCancel={() => setRemoving(null)}
        >
          <p>
            {formatMoney(removing.amount)} previsti per il {formatDate(removing.receiveOn)}. La
            rimozione resta nello storico.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

/** A cost, new or changed: the date of pagamento proposed from the terms, editable. */
function CostDialog({
  job,
  options,
  cost,
  onClose,
  onSaved,
}: {
  job: JobResponse;
  options: PassiveCycleResponse['options'];
  cost: JobCost | null;
  onClose: () => void;
  onSaved: (data: PassiveCycleResponse) => Promise<unknown>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const locked = cost !== null && (cost.state === 'consuntivato' || cost.state === 'pagato');
  const [description, setDescription] = useState(cost?.description ?? '');
  // The rate of the ciclo attivo, to start the cost from one of them (2.48): only a help.
  const canJobs = useCan('commesse.read');
  const active = useQuery({
    queryKey: [...jobKeys.all, 'active-cycle', job.id],
    queryFn: () => apiRequest<ActiveCycleResponse>(`/jobs/${job.id}/active-cycle`),
    enabled: canJobs && job.kind === 'commessa',
  });
  const rate = active.data?.installments ?? [];
  // One or more suppliers (2.28): the first gives the terms of payment.
  const [suppliers, setSuppliers] = useState<(string | null)[]>(
    cost?.suppliers.length ? cost.suppliers.map((supplier) => supplier.id) : [null],
  );
  const supplierIds = [...new Set(suppliers.filter((id): id is string => Boolean(id)))];
  const [receiveOn, setReceiveOn] = useState(cost?.receiveOn ?? job.startOn ?? '');
  const [termsCode, setTermsCode] = useState(cost?.termsCode ?? '');
  const [payOn, setPayOn] = useState(cost?.payOn ?? '');
  const [payTouched, setPayTouched] = useState(false);
  const [amount, setAmount] = useState(cost?.amount ?? '');
  const [vatRate, setVatRate] = useState(cost?.vatRate ?? '22.00');
  // Empty on a new cost: the server takes it from the last invoice of the supplier (2.05).
  const [withholding, setWithholding] = useState(cost?.withholdingRate ?? '');
  // With terms chosen here the date follows them; without, the server takes the supplier's.
  const proposed = (() => {
    const terms = options.terms.find((item) => item.code === termsCode);
    return receiveOn && terms ? dueFromTerms(receiveOn, terms.terms) : '';
  })();
  const shownPay = payTouched ? payOn : proposed || (cost && !termsCode ? '' : payOn);
  const save = useMutation({
    mutationFn: (body: JobCostRequest) =>
      cost
        ? apiRequest<PassiveCycleResponse>(`/jobs/${job.id}/costs/${cost.id}`, {
            method: 'PUT',
            body,
          })
        : apiRequest<PassiveCycleResponse>(`/jobs/${job.id}/costs`, { method: 'POST', body }),
    onSuccess: async (data) => {
      await onSaved(data);
      onClose();
    },
  });
  // «Dal listino passività» or «libero» (2.51): a new cost starts from the listino.
  const pricing = usePricing(job.id);
  const voices = pricing.data?.costOptions ?? [];
  const [mode, setMode] = useState<'voice' | 'free'>(cost && !cost.voice ? 'free' : 'voice');
  const [voiceCode, setVoiceCode] = useState(cost?.voice?.code ?? '');
  const [editions, setEditions] = useState(String(cost?.voice?.editions ?? 1));
  const [quantity, setQuantity] = useState(cost?.voice ? String(Number(cost.voice.quantity)) : '1');
  const [unitCost, setUnitCost] = useState(cost?.voice?.unitCost ?? '');
  // The unit of a cost without a voice (2.54).
  const [unitCode, setUnitCode] = useState(cost?.voice?.unitCode ?? '');
  const units = pricing.data?.units ?? [];
  const shownUnit = unitCode || units.find((unit) => unit.isDefault)?.code || units[0]?.code || '';
  const [isExpense, setIsExpense] = useState(cost?.isExpense ?? false);
  // Scadenzario (3.25): contracted with the supplier, and data fissa or all’erogazione.
  const [contracted, setContracted] = useState(cost?.contracted ?? false);
  const [kind, setKind] = useState<InstallmentKind>(cost?.kind ?? 'data_fissa');
  const option = voices.find((item) => item.code === voiceCode);
  const voiceAmount =
    parseDecimal(quantity) !== null && moneyOf(unitCost) !== null
      ? costTotal(
          parseDecimal(quantity)!,
          moneyOf(unitCost)!,
          Math.max(1, Math.trunc(parseDecimal(editions) ?? 1)),
        )
      : null;
  const chooseVoice = (code: string) => {
    const chosen = voices.find((item) => item.code === code);
    const before = voices.find((item) => item.code === voiceCode);
    setVoiceCode(code);
    if (chosen?.unitCost) setUnitCost(chosen.unitCost);
    // The description follows the voice until it is written by hand.
    if (chosen && (!description.trim() || description === before?.description)) {
      setDescription(chosen.description);
    }
  };
  const groups = [...new Set(voices.map((item) => item.typeCode))].map((typeCode) => ({
    label: typeCode
      ? (pricing.data?.types.find((type) => type.code === typeCode)?.description ?? typeCode)
      : 'Trasversali',
    options: voices.filter((item) => item.typeCode === typeCode),
  }));
  const money = moneyOf(amount);
  // A voice of the listino, or none (2.54): quantity × cost in a unit chosen here.
  const composed = mode === 'voice' && pricing.data !== undefined;
  const ready =
    description.trim() &&
    receiveOn &&
    (composed
      ? (Boolean(option) || Boolean(shownUnit)) && voiceAmount !== null && Number(voiceAmount) !== 0
      : money !== null && Number(money) !== 0);
  // What keeps Salva off, said in words (2.54).
  const missing = [
    !description.trim() && 'la descrizione',
    !receiveOn && 'la data di ricevimento',
    ...(composed
      ? [
          (parseDecimal(quantity) ?? 0) <= 0 && 'la quantità',
          moneyOf(unitCost) === null && 'il costo unitario',
          voiceAmount !== null && Number(voiceAmount) === 0 && 'un imponibile diverso da zero',
        ]
      : [(money === null || Number(money) === 0) && 'l’imponibile']),
  ].filter((item): item is string => Boolean(item));

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>{cost ? `Costo «${cost.description}»` : 'Nuovo costo'}</h2>
        {pricing.data && (
          <ModeChoice
            legend="Come si calcola il costo"
            value={mode}
            onChange={(value) => {
              setMode(value);
              if (value === 'free' && voiceAmount !== null) setAmount(voiceAmount);
            }}
            options={[
              {
                value: 'voice',
                label: 'Quantità × costo',
                hint: 'una voce del listino passività, o quantità e costo scritti da te',
                disabled: locked,
              },
              {
                value: 'free',
                label: 'Importo scritto a mano',
                hint: 'scrivi solo l’imponibile',
                disabled: locked,
              },
            ]}
          />
        )}
        {locked && (
          <p className="notice info">
            Consuntivato: importo e date sono quelli delle fatture. Si cambiano descrizione e
            fornitore.
          </p>
        )}
        {rate.length > 0 && (
          <Field
            label="Dal ciclo attivo"
            hint="Riprende la descrizione e la data della rata; poi puoi cambiarle o scrivere un testo libero."
          >
            <select
              value=""
              onChange={(event) => {
                const rata = rate.find((item) => item.id === event.target.value);
                if (!rata) return;
                setDescription(rata.description);
                if (!locked) setReceiveOn(rata.issueOn);
              }}
            >
              <option value="">Scegli una rata…</option>
              {rate.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.description} · {formatMoney(item.amount)} · {formatDate(item.issueOn)}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Descrizione">
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={200}
          />
        </Field>
        {suppliers.map((supplierId, index) => (
          <div key={index} className="stack tight">
            <ClientPicker
              label={index === 0 ? 'Fornitore' : `Altro fornitore (${index + 1})`}
              role="fornitore"
              value={supplierId}
              onChange={(id) =>
                setSuppliers(suppliers.map((item, at) => (at === index ? id : item)))
              }
              hint={
                index === 0
                  ? 'Tra i soggetti con il ruolo Fornitore. Senza termini scelti qui, il pagamento segue i suoi.'
                  : undefined
              }
              returnTo={`/commesse/${job.id}`}
            />
            {index > 0 && (
              <button
                type="button"
                className="link"
                onClick={() => setSuppliers(suppliers.filter((_, at) => at !== index))}
              >
                Togli questo fornitore
              </button>
            )}
          </div>
        ))}
        {suppliers.every(Boolean) && suppliers.length < 10 && (
          <button type="button" className="link" onClick={() => setSuppliers([...suppliers, null])}>
            + Un altro fornitore
          </button>
        )}
        <div className="form-grid">
          <Field label="Ricevimento previsto">
            <input
              type="date"
              value={receiveOn}
              disabled={locked}
              onChange={(event) => setReceiveOn(event.target.value)}
            />
          </Field>
          <Field label="Termini di pagamento" hint="Vuoto: quelli del fornitore in Anagrafica.">
            <select
              value={termsCode}
              disabled={locked}
              onChange={(event) => setTermsCode(event.target.value)}
            >
              <option value="">Dall’Anagrafica</option>
              {options.terms.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Pagamento previsto"
            hint={payTouched ? undefined : 'Dai termini; vuoto: lo calcola dal fornitore.'}
          >
            <input
              type="date"
              value={shownPay}
              disabled={locked}
              onChange={(event) => {
                setPayTouched(true);
                setPayOn(event.target.value);
              }}
            />
          </Field>
          {composed ? (
            <>
              <Field
                label="Voce"
                hint={`Listino passività ${pricing.data?.costList.name ?? 'Base'}.`}
              >
                <select
                  value={voiceCode}
                  disabled={locked}
                  onChange={(event) => chooseVoice(event.target.value)}
                >
                  <option value="">Nessuna voce: quantità e costo scritti da te</option>
                  {groups.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.description}
                          {item.unitCost &&
                            ` · ${formatMoney(item.unitCost)} / ${item.unitCode.toLowerCase()}`}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>
              {!option && (
                <Field label="Unità">
                  <select
                    value={shownUnit}
                    disabled={locked}
                    onChange={(event) => setUnitCode(event.target.value)}
                  >
                    {units.map((unit) => (
                      <option key={unit.code} value={unit.code}>
                        {unit.description}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Edizioni">
                <DecimalInput
                  value={editions}
                  onChange={setEditions}
                  decimals={[0, 0]}
                  disabled={locked}
                />
              </Field>
              <Field label={`Quantità${option ? ` (${option.unitCode.toLowerCase()})` : ''}`}>
                <DecimalInput
                  value={quantity}
                  onChange={setQuantity}
                  decimals={[0, 3]}
                  disabled={locked}
                />
              </Field>
              <Field
                label="Costo unitario"
                hint={
                  option?.unitCost && moneyOf(unitCost) !== option.unitCost
                    ? `listino ${formatMoney(option.unitCost)}`
                    : undefined
                }
              >
                <DecimalInput
                  value={unitCost}
                  onChange={setUnitCost}
                  decimals={[2, 2]}
                  disabled={locked}
                />
              </Field>
              <Field label="Imponibile" hint="Edizioni × quantità × costo unitario.">
                <input value={voiceAmount !== null ? formatMoney(voiceAmount) : '—'} disabled />
              </Field>
            </>
          ) : (
            <Field label="Imponibile">
              <DecimalInput
                value={amount}
                onChange={setAmount}
                decimals={[2, 2]}
                disabled={locked}
              />
            </Field>
          )}
          <Field label="Aliquota IVA">
            <select
              value={vatRate}
              disabled={locked}
              onChange={(event) => setVatRate(event.target.value)}
            >
              {[...new Map(options.vatRates.map((item) => [item.rate, item])).values()].map(
                (item) => (
                  <option key={item.code} value={item.rate}>
                    {item.label}
                  </option>
                ),
              )}
              {!options.vatRates.some((item) => item.rate === vatRate) && (
                <option value={vatRate}>{vatRate} %</option>
              )}
            </select>
          </Field>
        </div>
        <Field
          label="Ritenuta d’acconto %"
          hint="Trattenuta sul pagamento (per esempio 20 per un professionista). Vuoto: come l’ultima fattura del fornitore."
        >
          <DecimalInput
            value={withholding}
            onChange={setWithholding}
            decimals={[0, 2]}
            disabled={locked}
          />
        </Field>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={isExpense}
            onChange={(event) => setIsExpense(event.target.checked)}
          />{' '}
          È una spesa (trasferte, vitto, rimborsi): fuori dal margine, si controlla con le spese
          dell’attivo
        </label>
        <Field
          label="Tipo"
          hint={
            kind === 'data_fissa'
              ? 'Si paga alla data prevista.'
              : 'Si paga con il via libera, quando la prestazione è erogata.'
          }
        >
          <select value={kind} onChange={(event) => setKind(event.target.value as InstallmentKind)}>
            {INSTALLMENT_KINDS.map((item) => (
              <option key={item} value={item}>
                {INSTALLMENT_KIND_LABELS[item]}
              </option>
            ))}
          </select>
        </Field>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={contracted}
            onChange={(event) => setContracted(event.target.checked)}
          />{' '}
          Contrattualizzato: il fornitore è impegnato, entra nello scadenzario come certo
        </label>
        {!ready && <MissingNote missing={missing} />}
        {save.isError && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!ready || save.isPending}
            onClick={() =>
              save.mutate({
                description,
                supplierIds,
                receiveOn,
                payOn: locked ? cost!.payOn : shownPay || null,
                termsCode: termsCode || null,
                ...(composed
                  ? {
                      voice: {
                        code: option ? voiceCode : null,
                        unitCode: option ? null : shownUnit,
                        editions: Math.max(1, Math.trunc(parseDecimal(editions) ?? 1)),
                        quantity: String(parseDecimal(quantity)),
                        unitCost: moneyOf(unitCost)!,
                      },
                      amount: null,
                    }
                  : { voice: null, amount: money! }),
                isExpense,
                vatRate,
                withholdingRate: withholding.trim() ? (moneyOf(withholding) ?? '0') : null,
                contracted,
                kind,
              })
            }
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function settledTitle(row: JobCost): string {
  if (!row.settled) return '';
  const day = formatDate(row.settled.at.slice(0, 10));
  return `Saldo del ${day}${row.settled.by ? ` da ${row.settled.by}` : ''}: previsti ${formatMoney(row.amount)}`;
}

/**
 * «Cerca fatture» (2.28): the received invoices of the suppliers of a cost
 * still free, or on the commessa without a cost; ticked ones go to the cost.
 * When they do not cover it, saldo or more invoices to come.
 */
/** The lines of a received invoice, read in the dialog that links it to a cost (2.54). */
function InvoiceLines({ invoiceId }: { invoiceId: string }) {
  const lines = useQuery({
    queryKey: [...jobKeys.all, 'invoice', invoiceId],
    queryFn: () => apiRequest<InvoiceJobsResponse>(`/jobs/invoices/${invoiceId}`),
  });
  if (lines.isPending) return <small className="muted block">Caricamento delle righe…</small>;
  if (lines.isError) return <small className="error block">{lines.error.message}</small>;
  if (!lines.data.lines.length) {
    return <small className="muted block">Le righe non sono ancora lette da Gestify.</small>;
  }
  return (
    <ul className="invoice-line-list small">
      {lines.data.lines.map((line) => (
        <li key={line.position}>
          {line.description} <strong>{formatMoney(line.amount)}</strong>
          {toCents(line.rest) !== 0 && (
            <span className="muted">
              {' '}
              ({formatMoney(line.base)} + cassa {formatMoney(line.rest)})
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

type Candidate = CostInvoicesResponse['invoices'][number];

function CostInvoicesDialog({
  job,
  cost,
  costs,
  onClose,
  onSaved,
}: {
  job: JobResponse;
  cost: JobCost;
  costs: JobCost[];
  onClose: () => void;
  onSaved: (data: PassiveCycleResponse) => Promise<unknown>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const found = useQuery({
    queryKey: [...jobKeys.all, 'cost-invoices', cost.id],
    queryFn: () => apiRequest<CostInvoicesResponse>(`/jobs/${job.id}/costs/${cost.id}/invoices`),
  });
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [settle, setSettle] = useState(false);
  // The rest of an invoice on the other costs (2.55): invoice → cost → amount.
  const [spread, setSpread] = useState<Record<string, Record<string, string>>>({});
  // The invoices whose lines are open (2.54).
  const [opened, setOpened] = useState<string[]>([]);
  const save = useMutation({
    mutationFn: (body: CostInvoicesRequest) =>
      apiRequest<PassiveCycleResponse>(`/jobs/${job.id}/costs/${cost.id}/invoices`, {
        method: 'POST',
        body,
      }),
    onSuccess: async (data) => {
      await onSaved(data);
      onClose();
    },
  });
  const open = Number(cost.amount) - Number(cost.invoiced);
  const available = (item: Candidate) => Number(item.free) + Number(item.unlinkedHere);
  const items = Object.entries(chosen).map(([invoiceId, amount]) => ({
    invoiceId,
    amount: moneyOf(amount),
  }));
  const added = items.reduce((total, item) => total + Number(item.amount ?? 0), 0);
  const after = Number(cost.invoiced) + added;
  const partial = Math.abs(after) < Math.abs(Number(cost.amount));
  // The other costs of the same commessa still waiting for invoices (2.55).
  const others = costs.filter(
    (other) =>
      other.id !== cost.id &&
      other.jobId === cost.jobId &&
      !other.settled &&
      other.state !== 'consuntivato' &&
      other.state !== 'pagato',
  );
  const othersOf = (item: Candidate) =>
    others.filter((other) => other.suppliers.some((supplier) => supplier.id === item.supplierId));
  const spreadOn = (costId: string, except: string) =>
    Object.entries(spread).reduce(
      (total, [invoiceId, parts]) =>
        invoiceId === except ? total : total + Number(moneyOf(parts[costId] ?? '') ?? 0),
      0,
    );
  const otherOpen = (other: JobCost, invoiceId: string) =>
    Number(other.amount) - Number(other.invoiced) - spreadOn(other.id, invoiceId);
  const spreadOf = (invoiceId: string) =>
    Object.values(spread[invoiceId] ?? {}).reduce(
      (total, amount) => total + Number(moneyOf(amount) ?? 0),
      0,
    );
  const restOf = (item: Candidate) =>
    available(item) - Number(moneyOf(chosen[item.invoiceId] ?? '') ?? 0) - spreadOf(item.invoiceId);
  const otherItems = Object.entries(spread).flatMap(([invoiceId, parts]) =>
    Object.entries(parts).map(([costId, amount]) => ({
      costId,
      invoiceId,
      amount: moneyOf(amount),
    })),
  );
  const spreading = (found.data?.invoices ?? []).filter(
    (item) =>
      item.invoiceId in chosen &&
      othersOf(item).length > 0 &&
      (restOf(item) > 0.005 || spreadOf(item.invoiceId) > 0),
  );
  const ready =
    items.length > 0 &&
    items.every((item) => item.amount !== null && Number(item.amount) !== 0) &&
    otherItems.every((item) => item.amount !== null && Number(item.amount) > 0) &&
    !(found.data?.invoices ?? []).some((item) => item.invoiceId in chosen && restOf(item) < -0.005);

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>Fatture per «{cost.description}»</h2>
        <p className="muted small">
          Fatture ricevute di {cost.suppliers.map((supplier) => supplier.name).join(', ')} con una
          parte ancora da imputare, o già sulla commessa ma senza costo. Da consuntivare:{' '}
          {formatMoney(open.toFixed(2))}.
        </p>
        {found.isError && <p className="error">{found.error.message}</p>}
        {!found.data ? (
          !found.isError && <p className="muted">Caricamento…</p>
        ) : found.data.invoices.length === 0 ? (
          <p className="muted">Nessuna fattura dei fornitori del costo da imputare.</p>
        ) : (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <th />
                  <th>Fattura</th>
                  <th>Fornitore</th>
                  <th>Data</th>
                  <th className="right">Imponibile</th>
                  <th className="right">Da imputare</th>
                  <th>Già su</th>
                  <th className="right">Su questo costo</th>
                </tr>
              </thead>
              <tbody>
                {found.data.invoices.map((item) => {
                  const ticked = item.invoiceId in chosen;
                  return (
                    <tr key={item.invoiceId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={ticked}
                          aria-label={`Fattura ${item.number}`}
                          onChange={(event) => {
                            const next = { ...chosen };
                            if (event.target.checked) {
                              // The smaller of what the invoice has and what the cost still waits for.
                              const left = open - added;
                              const proposed =
                                left > 0 ? Math.min(available(item), left) : available(item);
                              next[item.invoiceId] = proposed.toFixed(2);
                              // The rest onto the other costs of the supplier, in the order of the rows.
                              let rest = available(item) - proposed;
                              const parts: Record<string, string> = {};
                              for (const other of othersOf(item)) {
                                const take = Math.min(rest, otherOpen(other, item.invoiceId));
                                if (take <= 0) continue;
                                parts[other.id] = take.toFixed(2);
                                rest -= take;
                              }
                              setSpread({ ...spread, [item.invoiceId]: parts });
                            } else {
                              delete next[item.invoiceId];
                              const kept = { ...spread };
                              delete kept[item.invoiceId];
                              setSpread(kept);
                            }
                            setChosen(next);
                          }}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="link"
                          aria-expanded={opened.includes(item.invoiceId)}
                          onClick={() =>
                            setOpened((current) =>
                              current.includes(item.invoiceId)
                                ? current.filter((id) => id !== item.invoiceId)
                                : [...current, item.invoiceId],
                            )
                          }
                        >
                          {opened.includes(item.invoiceId) ? '▾' : '▸'}{' '}
                          <CreditNoteMark kind={item.kind} />
                          {item.number}
                        </button>{' '}
                        <a
                          href={`/tesoreria/fatture/${item.invoiceId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="small"
                        >
                          Apri
                        </a>
                        {opened.includes(item.invoiceId) && (
                          <InvoiceLines invoiceId={item.invoiceId} />
                        )}
                      </td>
                      <td>{item.supplier}</td>
                      <td>{formatDate(item.date)}</td>
                      <td className="right">{formatMoney(item.taxable)}</td>
                      <td className="right">
                        {formatMoney(available(item).toFixed(2))}
                        {Number(item.unlinkedHere) !== 0 && (
                          <small className="muted block">
                            {formatMoney(item.unlinkedHere)} già qui senza costo
                          </small>
                        )}
                      </td>
                      <td className="muted small">
                        {item.elsewhere.length === 0
                          ? '—'
                          : item.elsewhere.map((part, index) => (
                              <div key={index}>
                                {part.code}
                                {part.cost && ` · ${part.cost}`}: {formatMoney(part.amount)}
                              </div>
                            ))}
                      </td>
                      <td className="right">
                        {ticked && (
                          <DecimalInput
                            value={chosen[item.invoiceId]!}
                            onChange={(amount) =>
                              setChosen({ ...chosen, [item.invoiceId]: amount })
                            }
                            decimals={[2, 2]}
                            aria-label={`Importo della fattura ${item.number}`}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {spreading.map((item) => {
          const parts = spread[item.invoiceId] ?? {};
          const rest = restOf(item);
          return (
            <div key={item.invoiceId} className="card stack tight">
              <strong>
                {rest > 0.005
                  ? `Restano ${formatMoney(rest.toFixed(2))} della fattura ${item.number}: mettili anche su altri costi`
                  : `La fattura ${item.number} anche su altri costi`}
              </strong>
              <small className="muted">
                Costi dello stesso fornitore ancora da consuntivare, proposti nell’ordine delle
                righe fino a esaurire la fattura.
              </small>
              <table className="lines">
                <thead>
                  <tr>
                    <th />
                    <th>Costo</th>
                    <th>Ricevimento</th>
                    <th className="right">Previsto</th>
                    <th className="right">Da consuntivare</th>
                    <th className="right">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {othersOf(item).map((other) => {
                    const ticked = other.id in parts;
                    return (
                      <tr key={other.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={ticked}
                            aria-label={`Costo ${other.description}`}
                            onChange={(event) => {
                              const next = { ...parts };
                              if (event.target.checked) {
                                const take = Math.min(
                                  Math.max(rest, 0),
                                  Math.max(otherOpen(other, item.invoiceId), 0),
                                );
                                next[other.id] = take.toFixed(2);
                              } else {
                                delete next[other.id];
                              }
                              setSpread({ ...spread, [item.invoiceId]: next });
                            }}
                          />
                        </td>
                        <td>{other.description}</td>
                        <td>{formatDate(other.receiveOn)}</td>
                        <td className="right">{formatMoney(other.amount)}</td>
                        <td className="right">
                          {formatMoney((Number(other.amount) - Number(other.invoiced)).toFixed(2))}
                        </td>
                        <td className="right">
                          {ticked && (
                            <DecimalInput
                              value={parts[other.id]!}
                              onChange={(amount) =>
                                setSpread({
                                  ...spread,
                                  [item.invoiceId]: { ...parts, [other.id]: amount },
                                })
                              }
                              decimals={[2, 2]}
                              aria-label={`Importo della fattura ${item.number} su ${other.description}`}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <small className={rest < -0.005 ? 'error' : 'muted'}>
                Fattura {item.number}: {formatMoney((available(item) - rest).toFixed(2))} imputati
                su {formatMoney(available(item).toFixed(2))} da imputare
                {rest < -0.005 && ': troppo, riduci gli importi'}
              </small>
            </div>
          );
        })}
        {items.length > 0 && (
          <div className="stack tight">
            <p>
              Dopo il collegamento: consuntivato {formatMoney(after.toFixed(2))} su{' '}
              {formatMoney(cost.amount)}
              {partial && `, restano ${formatMoney((Number(cost.amount) - after).toFixed(2))}`}.
            </p>
            {partial && (
              <>
                <label className="checkbox">
                  <input type="radio" checked={!settle} onChange={() => setSettle(false)} /> Ne
                  arriveranno altre: il resto resta previsto
                </label>
                <label className="checkbox">
                  <input type="radio" checked={settle} onChange={() => setSettle(true)} /> Saldo: il
                  costo si chiude a {formatMoney(after.toFixed(2))}, il resto esce dal previsto
                </label>
              </>
            )}
          </div>
        )}
        {save.isError && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!ready || save.isPending}
            onClick={() =>
              save.mutate({
                items: items.map((item) => ({ invoiceId: item.invoiceId, amount: item.amount! })),
                settle: partial && settle,
                others: otherItems.map((item) => ({ ...item, amount: item.amount! })),
              })
            }
          >
            {save.isPending ? 'Attendere…' : 'Collega'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/** «Collega al costo…» (2.28): the part of an invoice on the commessa without a cost goes to one. */
function LinkToCostDialog({
  job,
  invoice,
  costs,
  onClose,
  onSaved,
}: {
  job: JobResponse;
  invoice: JobReceivedInvoice;
  costs: JobCost[];
  onClose: () => void;
  onSaved: (data: PassiveCycleResponse) => Promise<unknown>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const [costId, setCostId] = useState(costs.length === 1 ? costs[0]!.id : '');
  const [amount, setAmount] = useState(invoice.amount);
  const [settle, setSettle] = useState(false);
  const cost = costs.find((item) => item.id === costId);
  const money = moneyOf(amount);
  const after = Number(cost?.invoiced ?? 0) + Number(money ?? 0);
  const partial = cost !== undefined && Math.abs(after) < Math.abs(Number(cost.amount));
  const save = useMutation({
    mutationFn: (body: CostInvoicesRequest) =>
      apiRequest<PassiveCycleResponse>(`/jobs/${invoice.jobId}/costs/${costId}/invoices`, {
        method: 'POST',
        body,
      }),
    onSuccess: async (data) => {
      await onSaved(data);
      onClose();
    },
  });
  // The cost must have the supplier of the invoice: the server says so if not.
  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Fattura {invoice.number}: a quale costo?</h2>
        <p className="muted small">
          {invoice.subject}, {formatMoney(invoice.amount)} su {job.code} senza costo. La fattura va
          su un costo che ha {invoice.subject} tra i fornitori.
        </p>
        {costs.length === 0 ? (
          <p className="notice info">
            Nessun costo aperto con un fornitore: sceglilo prima nel costo («Scegli il fornitore…»).
          </p>
        ) : (
          <>
            <Field label="Costo">
              <select value={costId} onChange={(event) => setCostId(event.target.value)}>
                <option value="">Scegli il costo…</option>
                {costs.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.description} · {formatMoney(item.amount)}
                    {Number(item.invoiced) !== 0 && ` (già ${formatMoney(item.invoiced)})`}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Imponibile sul costo">
              <DecimalInput value={amount} onChange={setAmount} decimals={[2, 2]} />
            </Field>
            {cost && (
              <p>
                Dopo il collegamento: consuntivato {formatMoney(after.toFixed(2))} su{' '}
                {formatMoney(cost.amount)}
                {partial && `, restano ${formatMoney((Number(cost.amount) - after).toFixed(2))}`}.
              </p>
            )}
            {partial && (
              <>
                <label className="checkbox">
                  <input type="radio" checked={!settle} onChange={() => setSettle(false)} /> Ne
                  arriveranno altre: il resto resta previsto
                </label>
                <label className="checkbox">
                  <input type="radio" checked={settle} onChange={() => setSettle(true)} /> Saldo: il
                  costo si chiude a {formatMoney(after.toFixed(2))}, il resto esce dal previsto
                </label>
              </>
            )}
          </>
        )}
        {save.isError && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!cost || money === null || Number(money) === 0 || save.isPending}
            onClick={() =>
              save.mutate({
                items: [{ invoiceId: invoice.invoiceId, amount: money! }],
                settle: partial && settle,
              })
            }
          >
            {save.isPending ? 'Attendere…' : 'Collega'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
